import { useNotify, useTranslate, useRecordContext } from 'react-admin';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  CircularProgress,
  Chip,
  ListSubheader,
} from '@mui/material';
import {
  type AssigneeChoice,
  splitRecipientSelection,
  resolveCertifiedAssignees,
} from '../../../../utils/certifiedUsersAssignments';
import { type DocumentAssignee } from '../../../../utils/certificationDocuments';
import { GrayAutocompleteInputWithUpperLabel } from './GrayAutocompleteInputWithUpperLabel';
import { notifyWithError } from '../../../../utils/errorHandling';

const ADD_USER_PICKER_SOURCE = '__addUserPicker';

type AddCertifiedUserPickerProps = {
  choices: AssigneeChoice[];
  tableAssetIds: Set<string>;
  disabled?: boolean;
  onAdd: (assignees: DocumentAssignee[]) => void;
};

export const AssigneeOptionText = () => {

  const record = useRecordContext<AssigneeChoice>();
  const translate = useTranslate();

  if (!record) return null;
  if (record.type === 'group') {

    return (
      <Box>
        {record.name}
        <Chip
          label={translate('resources.groups.text.members', { smart_count: record.membersCount })}
          variant="outlined"
          color="primary"
          size="small"
          sx={{ ml: 1 }}
        />
      </Box>
    );

  }
  return <span>{record.name}</span>;

};

export const getAssigneeChoiceLabel = (choice: AssigneeChoice) => {

  if (choice.type === 'group') {

    return `${choice.name} (${choice.membersCount})`;

  }
  return choice.name;

};

export const assigneeChoiceGroupBy = (
  option: AssigneeChoice,
  translate: (key: string) => string,
) => (
  option.type === 'group'
    ? translate('menu.items.groups')
    : translate('menu.items.management_users')
);

export const AssigneeChoiceGroup = (params: {
  group: string;
  children?: React.ReactNode;
}) => (
  <li>
    <ListSubheader
      component="div"
      sx={{ fontWeight: 800, lineHeight: '24px', color: '#000000' }}
    >
      {params.group}
    </ListSubheader>
    <ul style={{ padding: 10 }}>{params.children}</ul>
  </li>
);

export const AddCertifiedUserPicker = (props: AddCertifiedUserPickerProps) => {

  const translate = useTranslate();
  const notify = useNotify();
  const { setValue } = useFormContext();
  const [isResolving, setIsResolving] = useState(false);

  const availableChoices = useMemo(
    () => props.choices.filter((choice) => {

      if (choice.type === 'user') return !props.tableAssetIds.has(choice.id);
      return true;

    }),
    [props.choices, props.tableAssetIds],
  );

  const clearPicker = () => {

    setValue(ADD_USER_PICKER_SOURCE, null, { shouldDirty: false, shouldValidate: false });

  };

  const handleChange = async (choiceId: string | null) => {

    if (!choiceId || isResolving) {

      clearPicker();
      return;

    }

    setIsResolving(true);

    try {

      const { userIds, groupIds } = splitRecipientSelection([choiceId], props.choices);
      const resolved = await resolveCertifiedAssignees(userIds, groupIds, {
        excludeAssetIds: props.tableAssetIds,
      });

      if (resolved.length === 0) {

        notify(
          translate('resources.certification-categories.text.all_users_already_certified'),
          { type: 'warning' },
        );
        return;

      }

      props.onAdd(resolved);

    } catch (error) {

      notifyWithError(notify, error);

    } finally {

      clearPicker();
      setIsResolving(false);

    }

  };

  return (
    <GrayAutocompleteInputWithUpperLabel
      source={ADD_USER_PICKER_SOURCE}
      label="resources.certification-categories.fields.add_users"
      choices={availableChoices}
      disabled={props.disabled || isResolving || availableChoices.length === 0}
      optionValue="id"
      optionText={<AssigneeOptionText />}
      inputText={(choice) => getAssigneeChoiceLabel(choice as AssigneeChoice)}
      matchSuggestion={(filter, choice) => getAssigneeChoiceLabel(choice as AssigneeChoice).toLowerCase().includes(filter.toLowerCase())}
      groupBy={(option) => assigneeChoiceGroupBy(option as AssigneeChoice, translate)}
      renderGroup={(params) => (
        <AssigneeChoiceGroup group={params.group} key={params.key}>
          {params.children}
        </AssigneeChoiceGroup>
      )}
      onChange={handleChange}
      helperText={false}
      fullWidth
      TextFieldProps={{
        InputProps: {
          endAdornment: isResolving
            ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
            : undefined,
        },
      }}
      componentsProps={{
        paper: { sx: { boxShadow: (theme) => theme.shadows[8] } },
      }}
    />
  );

};
